import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { SubsidyRequest } from '@trnsact/trnsact-shared-types';
import { REQUEST_SUBSIDY } from '../api';
import { SubsidyTableRow } from '../types';

interface RequestSubsidyParams {
  creditSubmissionId: string;
  financeProgramId: string;
  equipmentId: string;
  term: number;
  subsidyPercentage: number;
  dnetPercentage: number;
  tableRow: SubsidyTableRow;
}

export function useRequestSubsidy() {
  const [submittedEquipmentIds, setSubmittedEquipmentIds] = useState<string[]>([]);
  const { showNotification } = useNotifications();
  const { handleOpen: openCertificate } = useModal(ModalsKeys.SubsidyCertificateDialog);

  const [requestSubsidyMutation, { loading: isRequesting }] = useMutation<{ requestSubsidy: SubsidyRequest }>(
    REQUEST_SUBSIDY
  );

  const requestSubsidy = async ({
    creditSubmissionId,
    financeProgramId,
    equipmentId,
    term,
    subsidyPercentage,
    dnetPercentage,
    tableRow,
    onSuccess,
  }: RequestSubsidyParams & { onSuccess?: () => void }): Promise<void> => {
    const dnetCoefficient = dnetPercentage / 100;
    const approvedSubsidyAmount =
      (subsidyPercentage / 100) * Math.min(tableRow.financeAmount, tableRow.dnet * dnetCoefficient);

    const { data, errors } = await requestSubsidyMutation({
      variables: {
        input: {
          creditSubmissionId,
          financeProgramId,
          equipmentId,
          term,
          subsidyPercentage,
          dnetPercentage,
          financeAmount: tableRow.financeAmount,
          dnet: tableRow.dnet,
          approvedSubsidyAmount,
        },
      },
    });

    if (errors?.length || !data?.requestSubsidy?.subsidyRequestId) {
      showNotification('Failed to request subsidy. Please try again.', { type: 'error' });
      return;
    }

    setSubmittedEquipmentIds(prev => [...prev, equipmentId]);
    onSuccess?.();
    openCertificate({ ...data.requestSubsidy, equipment: data.requestSubsidy.equipment ?? null });
  };

  return { requestSubsidy, submittedEquipmentIds, isRequesting };
}
